import { get } from 'lodash-es';
import {
  type Address,
  type ContractFunctionExecutionError,
  type TransactionExecutionError,
} from 'viem';
import { goerli, mainnet, optimism, optimismGoerli } from 'viem/chains';
import type { PublicClient } from 'wagmi';
import { assertAddress } from '@soundxyz/utils/address';
import { humanReadableChainName } from './helpers';

export function chainFromId(chainId: number) {
  switch (chainId) {
    case 10:
      return optimism;
    case 1:
      return mainnet;
    case 420:
      return optimismGoerli;
    case 5:
      return goerli;
    default: {
      throw Error(`Unsupported chain id ${chainId}`);
    }
  }
}

export function isInsufficientFundsError(
  error: unknown,
): error is ContractFunctionExecutionError | TransactionExecutionError {
  const detailsErrorMessage: unknown = get(error, 'cause.details');
  const rootErrorMessage: unknown = get(error, 'message');

  return (
    (typeof detailsErrorMessage === 'string' &&
      detailsErrorMessage.toLowerCase().includes('insufficient funds')) ||
    (typeof rootErrorMessage === 'string' &&
      rootErrorMessage.toLowerCase().includes('insufficient funds'))
  );
}

const DEFAULT_ETHERS_MAX_FEE_PER_GAS = 2_000_000_000n;
const DEFAULT_OPTIMISM_MAX_FEE_PER_GAS = 1_000_000n;

export async function hasEnoughForGasEstimate({
  gasEstimate,
  userAddress,
  publicClient,
  extraValue,
  chainId,
}: {
  gasEstimate: bigint;
  userAddress: Address;
  publicClient: PublicClient;
  extraValue: bigint | null;
  chainId: number;
}) {
  const value = extraValue ?? 0n;

  const [balance, baseFeePerGas] = await Promise.all([
    publicClient.getBalance({
      address: userAddress,
      blockTag: 'latest',
    }),
    publicClient
      .getBlock({
        blockTag: 'pending',
      })
      .then(v => v.baseFeePerGas ?? 0n),
  ]);

  const totalGasEstimate =
    gasEstimate *
    (baseFeePerGas +
      (humanReadableChainName(chainId) === 'Optimism'
        ? DEFAULT_OPTIMISM_MAX_FEE_PER_GAS
        : DEFAULT_ETHERS_MAX_FEE_PER_GAS));

  return balance >= totalGasEstimate + value;
}

export async function hasEnoughForGasEstimateV2({
  userAddress,
  publicClient,
  isOptimism,
}: {
  userAddress: Address;
  publicClient: PublicClient;
  isOptimism: boolean;
}) {
  const [balance, gasPrice] = await Promise.all([
    publicClient.getBalance({
      address: userAddress,
      blockTag: 'latest',
    }),
    publicClient.getGasPrice(),
  ]);

  const totalGasEstimate =
    gasPrice * (isOptimism ? DEFAULT_OPTIMISM_MAX_FEE_PER_GAS : DEFAULT_ETHERS_MAX_FEE_PER_GAS);

  return balance >= totalGasEstimate;
}

export async function isDeployedContractAddress({
  contractAddress,
  provider,
}: {
  contractAddress: string;
  provider: PublicClient;
}) {
  assertAddress(contractAddress);

  const byteCode = await provider.getBytecode({
    address: contractAddress,
  });

  return byteCode != null && byteCode !== '0x';
}
